import { useThemedHex } from '@/hooks/use-theme-colors';
import { PaymentDetailsModal } from '@/components/charges/payment-details-modal';
import { ProofUploadModal } from '@/components/charges/proof-upload-modal';
import React, { useState } from 'react';
import { DashboardScrollView } from '@/components/dashboard-scroll-view';
import { Pressable, ActivityIndicator, View } from 'react-native';
import { PageHeader } from '@/components/ui/page-header';
import { useEnrollment } from '@/hooks/use-enrollments';
import { Charge } from '@/types/charge';
import { CHARGE_STATUS } from '@/types/charge-status';
import { ENROLLMENT_STATUS } from '@/types/enrollment-status';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate } from '@/utils/format-date';
import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import {
  SectionCard,
  SectionDivider,
  StatRow,
  StatTile,
  type SectionTone,
} from '@/components/ui/section-card';

/** Rótulo + tom do status do contrato, na mesma paleta dos outros blocos. */
const CONTRACT_STATUS_META: Record<ENROLLMENT_STATUS, { label: string; tone: SectionTone }> = {
  [ENROLLMENT_STATUS.ACTIVE]: { label: 'Ativo', tone: 'success' },
  [ENROLLMENT_STATUS.PAUSED]: { label: 'Pausado', tone: 'warning' },
  [ENROLLMENT_STATUS.INACTIVE]: { label: 'Inativo', tone: 'muted' },
  [ENROLLMENT_STATUS.PENDING_APPROVAL]: { label: 'Aguardando aprovação', tone: 'warning' },
  [ENROLLMENT_STATUS.COMPLETED]: { label: 'Concluído', tone: 'muted' },
  [ENROLLMENT_STATUS.CANCELLED]: { label: 'Cancelado', tone: 'danger' },
};

export default function ClientEnrollmentDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  // Include refetch to update after modal actions
  const { data: enrollment, isLoading, error, refetch } = useEnrollment(id);
  const [selectedCharge, setSelectedCharge] = useState<Charge | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isProofModalVisible, setIsProofModalVisible] = useState(false);

  if (isLoading) {
    return (
      <View className="flex-1 items-center justify-center bg-canvas">
        <ActivityIndicator size="large" className="text-primary" />
        <ThemedText className="mt-2 text-foreground">Carregando detalhes...</ThemedText>
      </View>
    );
  }

  if (error || !enrollment) {
    return (
      <View className="flex-1 items-center justify-center bg-canvas p-5">
        <Ionicons
          name="alert-circle-outline"
          size={48}
          className="mb-4 text-error-600"
        />
        <ThemedText className="mb-4 text-center text-foreground">
          {error ? 'Erro ao carregar contrato.' : 'Contrato não encontrado.'}
        </ThemedText>
        <Button
          title="Voltar"
          onPress={() => router.back()}
          variant="link"
        />
      </View>
    );
  }

  const sortedCharges = enrollment.charges
    ? [...enrollment.charges].sort((a, b) => {
        const priority: Record<string, number> = {
          [CHARGE_STATUS.OVERDUE]: 0,
          [CHARGE_STATUS.PENDING]: 1,
          [CHARGE_STATUS.PAYMENT_REPORTED]: 2,
          [CHARGE_STATUS.IN_DISPUTE]: 3,
          [CHARGE_STATUS.PAID]: 4,
          [CHARGE_STATUS.CANCELED]: 5,
        };

        const priorityA = priority[a.status] ?? 99;
        const priorityB = priority[b.status] ?? 99;

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        // Secondary sort by due date (asc for overdue/pending, desc for paid)
        if (priorityA <= 1) {
             return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        }
        return new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime();
      })
    : [];

  const handleChargePress = (charge: Charge) => {
    setSelectedCharge(charge);
    setIsModalVisible(true);
  };

  const statusMeta = CONTRACT_STATUS_META[enrollment.status] ?? {
    label: enrollment.status,
    tone: 'muted' as SectionTone,
  };

  return (
    <View className="flex-1 bg-canvas pt-5">
      <View className="px-5 mb-5 flex-row items-center gap-4">
        <Ionicons
            name="arrow-back"
            size={24}
            className="text-foreground"
            onPress={() => router.back()}
        />
        <View className="flex-1">
            <PageHeader
                title={enrollment.service?.name || 'Detalhes do Serviço'}
                className="mb-0"
            />
        </View>
      </View>

      <DashboardScrollView className="flex-1 w-full bg-canvas" contentContainerStyle={{ paddingBottom: 80 }}>
        <View className="px-5 gap-6">
          {/* O contrato: quanto custa e por quanto tempo */}
          <SectionCard
            icon="document-text-outline"
            title="Seu contrato"
            badge={statusMeta.label}
            badgeTone={statusMeta.tone}
          >
            <View className="gap-0.5">
              <ThemedText className="text-2xl font-bold text-neutral-900">
                {formatCurrency(enrollment.price)}
              </ThemedText>
              <ThemedText className="text-xs text-neutral-500">
                {enrollment.status === ENROLLMENT_STATUS.PENDING_APPROVAL
                  ? 'O prestador ainda pode recusar este contrato.'
                  : 'Valor acertado com o prestador.'}
              </ThemedText>
            </View>

            <SectionDivider />

            <StatRow>
              <StatTile label="Início" value={formatDate(enrollment.startDate)} />
              <StatTile
                label="Término"
                value={enrollment.endDate ? formatDate(enrollment.endDate) : 'Sem data'}
              />
            </StatRow>
          </SectionCard>

          {/* Histórico de cobranças */}
          <SectionCard
            icon="receipt-outline"
            title="Histórico de cobranças"
            badge={`${sortedCharges.length}`}
            badgeTone="muted"
          >
            {sortedCharges.length > 0 ? (
                <View className="gap-2.5">
                    {sortedCharges.map((charge) => (
                        <ChargeItem
                          key={charge.id}
                          charge={charge}
                          onPress={() => handleChargePress(charge)}
                        />
                    ))}
                </View>
            ) : (
                <ThemedText className="text-sm leading-5 text-neutral-500">
                  Nenhuma cobrança registrada até agora.
                </ThemedText>
            )}
          </SectionCard>
        </View>
      </DashboardScrollView>

      <PaymentDetailsModal
        open={isModalVisible}
        charge={selectedCharge}
        onOpenChange={setIsModalVisible}
        onReportPaymentSuccess={() => {
            refetch();
            setIsModalVisible(false);
        }}
        onOpenProofModal={() => {
            setIsModalVisible(false);
            setTimeout(() => setIsProofModalVisible(true), 100);
        }}
      />
      
      {selectedCharge && (
          <ProofUploadModal
              chargeId={selectedCharge.id}
              open={isProofModalVisible}
              onOpenChange={setIsProofModalVisible}
              onSuccess={() => {
                  refetch();
                  setIsProofModalVisible(false);
                  // Optional: Re-open Details modal to show confirmation or just stay on list
              }}
          />
      )}
    </View>
  );
}

/** Rótulo + cor do status, na mesma paleta dos outros blocos. */
const getStatusDetails = (status: CHARGE_STATUS, th: (hex: string) => string) => {
    switch (status) {
        case CHARGE_STATUS.PAID:
            return { label: 'Pago', bg: th('#ECFDF5'), color: th('#047857') };
        case CHARGE_STATUS.PENDING:
            return { label: 'Pendente', bg: th('#FFF7ED'), color: th('#C2410C') };
        case CHARGE_STATUS.OVERDUE:
            return { label: 'Vencido', bg: th('#FEF2F2'), color: th('#B91C1C') };
        case CHARGE_STATUS.CANCELED:
            return { label: 'Cancelado', bg: th('#F5F5F5'), color: th('#737373') };
        case CHARGE_STATUS.IN_DISPUTE:
            return { label: 'Em disputa', bg: th('#FFFBEB'), color: th('#B45309') };
        case CHARGE_STATUS.PAYMENT_REPORTED:
            return { label: 'Pagamento informado', bg: th('#FFF7ED'), color: th('#C2410C') };
        default:
            return { label: status, bg: th('#F5F5F5'), color: th('#737373') };
    }
};

function ChargeItem({ charge, onPress }: { charge: Charge; onPress: () => void }) {
    const th = useThemedHex();
    const statusDetails = getStatusDetails(charge.status, th);

    return (
        <Pressable
          onPress={onPress}
          className="flex-row items-center gap-3 rounded-2xl bg-neutral-50 p-3 active:opacity-70"
          style={{ borderCurve: 'continuous' }}
        >
            <View className="min-w-0 flex-1 gap-1">
                <ThemedText className="text-base font-bold text-neutral-900">
                    {formatCurrency(charge.amount)}
                </ThemedText>
                <ThemedText className="text-xs text-neutral-500">
                    {charge.paidAt
                      ? `Pago em ${formatDate(charge.paidAt)}`
                      : `Vence em ${formatDate(charge.dueDate)}`}
                </ThemedText>
            </View>
            <View
              className="rounded-full px-2.5 py-1"
              style={{ backgroundColor: statusDetails.bg }}
            >
                <ThemedText
                  className="text-xs font-semibold"
                  style={{ color: statusDetails.color }}
                >
                    {statusDetails.label}
                </ThemedText>
            </View>
            <Ionicons className="text-neutral-400" name="chevron-forward" size={16} />
        </Pressable>
    );
}

